import React from "react";
import { Card } from "react-onsenui";
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  Tooltip,
  LabelList,
  ResponsiveContainer,
} from "recharts";
import db from "../../data/universe.json"; // Direct import of JSON data



const glossyColors = {
  "Cannabinoids": "#32CD32",
  "Psychedelics": "#8A2BE2",
  "Stimulant Psychedelics": "#FF1493",
  "Natural stimulants": "#F1AA66",
  "Pharmaceutical stimulants": "#FF5B5D",
  "Stimulants": "#D61314",
  "Antidepressants": "#F1E066",
  "Antipsychotics": "#BBD02A",
  "Benzodiazepines": "#00BFAE",
  "Barbiturates": "#64B5F6",
  "Depressants": "#2196F3",
  "Opioids": "#4682B4",
  "Dissociatives": "#9E9E9E",
  "Deliriants": "#CD5C5C"
};

const CustomTooltip = React.memo(({ payload, active, coordinate, chartWidth, chartHeight, zoomLevel }) => {
  if (active && payload && payload.length) {
    const { name, category, classes } = payload[0].payload;
    
    const isMobile = window.innerWidth <= 768;
    const scale = isMobile ? 1 : 1 / zoomLevel;

    const tooltipStyle = {
      position: 'absolute',
      backgroundColor: 'white',
      border: '1px solid #ccc',
      borderRadius: '4px',
      padding: '10px',
      boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
      pointerEvents: 'none',
      zIndex: 1000,
      whiteSpace: 'nowrap',
      transform: `scale(${scale})`,
      transformOrigin: 'top left',
    };

    // Different logic for mobile vs. large screens
    let tooltipX, tooltipY;

    if (isMobile) {
      // Mobile-friendly logic (previous approach)
      tooltipX = coordinate ? coordinate.x : 0;
      tooltipY = coordinate ? coordinate.y : 0;
    } else {
      // Large screen-friendly logic (new approach)
      tooltipX = Math.min(coordinate.x, chartWidth - 220); // Keep some space on the right
      tooltipY = Math.min(coordinate.y, chartHeight - 100); // Prevent overflow on the bottom
    }

    return (
      <div
        style={{
          ...tooltipStyle,
          left: `${tooltipX}px`,
          top: `${tooltipY}px`,
        }}
      >
        <div style={{ fontWeight: 'bold' }}>{name}</div>
        <div>Category: {category}</div>
        <div>Classes:</div>
        <ul style={{ paddingLeft: '20px' }}>
          {classes.map((cls, index) => (
            <li key={index} style={{ color: 'gray', fontSize: '0.9em' }}>{cls}</li>
          ))}
        </ul>
      </div>
    );
  }
  return null;
});

const CustomLegend = ({ categories }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', paddingBottom: '10px' }}>
    {categories.map((category) => (
      <span
        key={category}
        style={{
          borderRadius: '5px',
          padding: '5px 10px',
          marginRight: '10px',
          marginBottom: '5px',
          display: 'inline-block',
          fontSize: '0.9em',
          backgroundColor: glossyColors[category],
          color: 'white'
        }}
      >
        {category}
      </span>
    ))}
  </div>
);

export default class SubstanceUniverse extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      data: db,
      zoomLevel: 1,
      isLegendVisible: false,
    };
  }

  // componentDidMount() {
  //   window.addEventListener('resize', this.handleZoom);
  //   this.handleZoom();
  // }
  
  // componentWillUnmount() {
  //   window.removeEventListener('resize', this.handleZoom);
  // }
  
  // handleZoom = () => {
  //   const zoomLevel = window.devicePixelRatio || 1;
  //   this.setState({ zoomLevel });
  // };

  handleInfoClick = () => {
    alert('Approximate map of similarities between major classes of psychoactive compounds. Inspired by Universe of Drugs made by Espolea.');
  };
  

  toggleLegendVisibility = () => {
    this.setState((prevState) => ({
      isLegendVisible: !prevState.isLegendVisible
    }));
  };

  getMinMaxValues() {
    const { data } = this.state;
    const xValues = data.map(d => d.x);
    const yValues = data.map(d => d.y);
    const xMin = Math.min(...xValues);
    const xMax = Math.max(...xValues);
    const yMin = Math.min(...yValues);
    const yMax = Math.max(...yValues);
    return { xMin, xMax, yMin, yMax };
  }

  renderScatterChart() {
    const { data } = this.state;
    const { substance } = this.props;
  
    if (!substance || !substance.commonName) {
      return null;
    }
  
    const { commonName } = substance;
    const highlightPoint = data.filter(
      (d) => d.name.toLowerCase() === commonName.toLowerCase()
    );
  
    const categoryColors = Object.keys(glossyColors).sort();
    const { xMin, xMax, yMin, yMax } = this.getMinMaxValues();
  
    return (
      <div style={{ position: "relative", height: "calc(100vw * 0.75)", maxHeight: "75vh", marginLeft: "-4%", marginRight: "-4%", marginBottom: "-4%" }}>
        <ResponsiveContainer width="100%" aspect={1}>
          <ScatterChart
            margin={{ top: 0, right: 20, bottom: 40, left: 20 }}
            style={{ display: "block", margin: "auto" }}
          >
            <XAxis
              type="number"
              dataKey="x"
              name="X-axis"
              domain={[xMin, xMax]}
              hide={true}
            />
            <YAxis
              type="number"
              dataKey="y"
              name="Y-axis"
              domain={[yMin, yMax]}
              hide={true}
            />
            <Tooltip
              content={
                <CustomTooltip
                  zoomLevel={this.state.zoomLevel}
                  chartWidth={window.innerWidth} // Dynamically pass the window width
                  chartHeight={window.innerHeight} // Dynamically pass the window height
                />
              }
              cursor={{ strokeDasharray: "3 3" }}
            />
            {categoryColors.map((category) => (
              <Scatter
                key={category}
                data={data.filter(
                  (d) =>
                    d.category === category &&
                    (!highlightPoint || d.name !== highlightPoint.name)
                )}
                fill={glossyColors[category]}
                shape={(props) => (
                  <circle
                    {...props}
                    r={window.innerWidth > 768 ? 10 : 5} // Larger radius on big screens
                    fill={props.fill}
                  />
                )}
              >
                <LabelList dataKey="name" position="center" fill="#000" fontSize={1} />
              </Scatter>
            ))}
  
            {highlightPoint.length > 0 && (
              <Scatter
                name="Highlight"
                data={highlightPoint}
                fill={glossyColors[highlightPoint[0].category]}
                shape={(props) => (
                  <circle
                    {...props}
                    r={window.innerWidth > 768 ? 15 : 7} // Larger radius for highlighted points on big screens
                    fill={props.fill}
                    stroke="red"
                    strokeWidth={15}
                  />
                )}
              />
            )}
          </ScatterChart>
        </ResponsiveContainer>
      </div>
    );
  }

  render() {
    const { isLegendVisible } = this.state;
    return (
      <Card className="simplified-list" style={{marginBottom: '10px'}}>
        <div style={{ margin: "10px 0px 15px", textAlign: "left" }}>
          {this.renderScatterChart()}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
  <span>Zoom or click for details.</span>
  <span 
    style={{ cursor: 'pointer', padding: '10px' }} // Increased padding to enlarge hit area
    onClick={this.handleInfoClick}  // This triggers the alert
  >
    <i className="zmdi zmdi-info-outline"></i>
  </span>
</div><div>
            <span
              onClick={this.toggleLegendVisibility}
              style={{ cursor: "pointer", textDecoration: "underline" }}
            >
              
              {isLegendVisible ? "Hide legend." : "Click here to view legend."}
            </span>
          </div>
          {isLegendVisible && (
            <div style={{ margin: '20px 0px -20px 0px' }}>
              <CustomLegend categories={Object.keys(glossyColors)} />
            </div>
          )}
        </div>
      </Card>
    );
  }
}
